import { Fragment, useMemo } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { cn } from '@/lib/core';
import type { EventDateFacetDto } from '@/services/platform/api/model';
import { addDays, eachDayKey, endOfMonth, todayKey, toRailDay } from './format';

/** Rail always spans at least this many days from today, even with sparse/near-term content. */
const MIN_RAIL_DAYS_AHEAD = 30;

export interface CalendarRailProps {
  /** Base dates with any event for the hub — extends the rail's range past `today + 30 days` when content runs later. */
  dates: EventDateFacetDto[];
  /** Days that also match the current category selection (all content days if no category selected). */
  enabledDateKeys: string[];
  /** The day key (`YYYY-MM-DD`) currently marked, if any. Marking is a scroll position, not a filter. */
  markedDate?: string;
  /** Fired ONCE per tap, with the tapped day — the caller marks it and jumps the list there. */
  onSelectDate: (dateKey: string) => void;
}

/**
 * Left vertical date rail. Always renders a continuous run of days from
 * today through the later of the last date that has events or
 * `today + 30 days` — never hidden or empty, even for a hub with no content
 * at all — with a sticky month header. A day is tappable only when it both
 * has content and matches the current category selection
 * (`enabledDateKeys`); other days are dimmed and disabled, per the ELP rule
 * "dates without associated content are not selectable".
 *
 * Tapping a day does NOT filter the list — it marks the day and jumps the list to it.
 * `onSelectDate` therefore fires exactly once per tap: a toggle-style callback would fire again
 * for the previously marked day and drag the list through a second jump.
 */
export function CalendarRail({ dates, enabledDateKeys, markedDate, onSelectDate }: CalendarRailProps) {
  const locale = useLocaleStore(state => state.locale);

  const contentKeys = useMemo(() => new Set(dates.map(d => d.date).filter((d): d is string => !!d)), [dates]);
  const enabledKeys = useMemo(() => new Set(enabledDateKeys), [enabledDateKeys]);

  const days = useMemo(() => {
    const sorted = [...contentKeys].sort();
    const lastContentKey = sorted[sorted.length - 1];
    const start = todayKey();
    const minEnd = addDays(start, MIN_RAIL_DAYS_AHEAD);
    const end = lastContentKey && lastContentKey > minEnd ? lastContentKey : minEnd;
    return eachDayKey(start, endOfMonth(end)).map(key => toRailDay(key, locale));
  }, [contentKeys, locale]);

  let lastMonth = '';

  return (
    <div className="h-full min-h-0 w-12.5 shrink-0 scrollbar-none overflow-y-auto pb-24">
      {/* Block + margin spacing (not flex `gap`) — `gap` under a sticky child causes 1px scroll jitter. */}
      <div className="space-y-2">
        {days.map(day => {
          const isSelected = day.dateKey === markedDate;
          // The marked day stays tappable — jumping back to it must keep working even if the
          // active categories would otherwise disable it.
          const isEnabled = isSelected || (contentKeys.has(day.dateKey) && enabledKeys.has(day.dateKey));
          const showMonth = day.monthKey !== lastMonth;
          lastMonth = day.monthKey;

          return (
            <Fragment key={day.dateKey}>
              {showMonth && (
                <div className="sticky top-0 z-10 flex w-full transform-gpu flex-col items-center justify-center bg-white py-3 will-change-transform backface-hidden">
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[20px] font-semibold whitespace-nowrap capitalize">
                    {day.monthShort}
                  </span>
                </div>
              )}
              <button
                type="button"
                disabled={!isEnabled}
                aria-pressed={isSelected}
                onClick={isEnabled ? () => onSelectDate(day.dateKey) : undefined}
                className={cn(
                  'flex w-full flex-col items-center justify-center py-2 transition-colors',
                  isSelected && 'bg-jncquoi-neutral-1000',
                  isEnabled ? 'cursor-pointer' : 'cursor-default'
                )}
              >
                <span
                  className={cn(
                    'font-pp-neue-montreal text-xl font-semibold',
                    isSelected
                      ? 'text-jncquoi-neutral-00'
                      : isEnabled
                        ? 'text-jncquoi-neutral-1000'
                        : 'text-jncquoi-neutral-600'
                  )}
                >
                  {day.day}
                </span>
                <span
                  className={cn(
                    'font-pp-neue-montreal text-sm font-normal tracking-wide',
                    isSelected
                      ? 'text-jncquoi-neutral-00'
                      : isEnabled
                        ? 'text-jncquoi-neutral-1000'
                        : 'text-jncquoi-neutral-600'
                  )}
                >
                  {day.weekday}
                </span>
              </button>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
